import { useEffect, useState } from "react";
import api, { formatErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Building2, Plus, Pencil, Archive } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", brand: "", region: "", cluster: "", city: "", country: "India" };

export default function Hotels() {
  const { user } = useAuth();
  const isAdmin = user.role === "corporate_admin";
  const [hotels, setHotels] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () => api.get("/hotels?include_archived=true").then((r) => setHotels(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editing) await api.put(`/hotels/${editing}`, form);
      else await api.post("/hotels", form);
      toast.success(editing ? "Hotel updated" : "Hotel added");
      setOpen(false);
      setForm(EMPTY);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const archive = async (h) => {
    await api.put(`/hotels/${h.id}/archive`);
    toast.success(h.archived ? "Hotel restored" : "Hotel archived");
    load();
  };

  const field = (key, label) => (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Input data-testid={`hotel-form-${key}`} value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} />
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto space-y-6" data-testid="hotels-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-stone-900">Hotels</h1>
          <p className="text-stone-500 mt-1 text-sm">Corporate → Region → Cluster → Hotel</p>
        </div>
        {isAdmin && (
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setForm(EMPTY); setEditing(null); } }}>
            <DialogTrigger asChild>
              <Button data-testid="add-hotel-button" className="bg-[#14532D] hover:bg-[#166534]"><Plus className="w-4 h-4 mr-2" />Add Hotel</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>{editing ? "Edit Hotel" : "Add Hotel"}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">{field("name", "Hotel Name")}</div>
                {field("brand", "Brand")}
                {field("region", "Region")}
                {field("cluster", "Cluster")}
                {field("city", "City")}
                {field("country", "Country")}
              </div>
              <Button data-testid="hotel-form-save" onClick={save} disabled={!form.name} className="bg-[#14532D] hover:bg-[#166534] w-full mt-2">Save</Button>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="bg-white border border-[#E7E5DF] rounded-xl overflow-x-auto">
        <table className="w-full min-w-[720px]">
          <thead className="bg-stone-50">
            <tr className="text-left text-[11px] uppercase tracking-wider text-stone-500 border-b border-[#E7E5DF]">
              <th className="py-3 px-4">Hotel</th>
              <th className="py-3 px-4">Brand</th>
              <th className="py-3 px-4">Region</th>
              <th className="py-3 px-4">Cluster</th>
              <th className="py-3 px-4">City</th>
              <th className="py-3 px-4">Status</th>
              {isAdmin && <th className="py-3 px-4 text-right">Actions</th>}
            </tr>
          </thead>
          <tbody data-testid="hotels-table">
            {hotels.map((h) => (
              <tr key={h.id} className={`border-b border-stone-100 last:border-0 hover:bg-stone-50/60 ${h.archived ? "opacity-50" : ""}`}>
                <td className="py-3.5 px-4 text-sm font-medium text-stone-800 flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-[#14532D]" strokeWidth={1.5} />{h.name}
                </td>
                <td className="py-3.5 px-4 text-sm text-stone-600">{h.brand}</td>
                <td className="py-3.5 px-4 text-sm text-stone-600">{h.region}</td>
                <td className="py-3.5 px-4 text-sm text-stone-600">{h.cluster}</td>
                <td className="py-3.5 px-4 text-sm text-stone-600">{h.city}</td>
                <td className="py-3.5 px-4">
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium border ${h.archived ? "bg-stone-100 text-stone-500 border-stone-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}>
                    {h.archived ? "Archived" : "Active"}
                  </span>
                </td>
                {isAdmin && (
                  <td className="py-3.5 px-4 text-right">
                    <button data-testid={`hotel-edit-${h.id}`} className="p-1.5 rounded hover:bg-stone-100" onClick={() => { setEditing(h.id); setForm({ name: h.name, brand: h.brand, region: h.region, cluster: h.cluster, city: h.city, country: h.country }); setOpen(true); }}>
                      <Pencil className="w-4 h-4 text-stone-500" strokeWidth={1.5} />
                    </button>
                    <button data-testid={`hotel-archive-${h.id}`} className="p-1.5 rounded hover:bg-stone-100" onClick={() => archive(h)}>
                      <Archive className="w-4 h-4 text-stone-500" strokeWidth={1.5} />
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
